import { isPlatformBrowser } from '@angular/common';

import {
  AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges
} from '@angular/core';

import type { IReinsurance } from '../../_interfaces/reinsurrance.interface';
import { SdReinsuranceComponent } from './reinsurance/reinsurance.component';
import { BrandThemesEnum, ThemesEnum } from '../../_enums/themes.enum';
import { IconSizeEnum } from '../../_enums/icon.enum';
import { PageTypeEnum } from '../../_enums/page-type.enum';
import { PositionEnum } from '../../_enums/position.enum';
import { SdButtonIconComponent } from '../buttons/button-icon/button-icon.component';
import { SdLinkComponent } from '../links/link/link.component';
import { SdModalDefaultComponent } from '../modals/modal-default/modal-default.component';
import { SdThemeComponent } from '../abstract/theme/theme.component';
import { ThemesTextEnum } from '../../_enums/themes-text.enum';
import { ThemesUtils } from '../../utils/themes.utils';
import { IReinsuranceModal, IReinsurancesModal } from '../../_interfaces/reinsurances.interface';
import { SdHtmlComponent } from '../html/html.component';

@Component({
  selector: 'sd-reinsurances',
  templateUrl: './reinsurances.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdButtonIconComponent,
    SdModalDefaultComponent,
    SdLinkComponent,
    SdReinsuranceComponent,
    SdHtmlComponent
  ]
})
export class SdReinsurancesComponent extends SdThemeComponent implements OnChanges, OnInit, AfterViewInit {
  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel: number;

  @Input()
  reinsurances: IReinsurance[];

  /**
   * Allow to define if the text is aligned to the left or the right, or even centered
   * * @param {string} left | right | center
   */
  @Input()
  textConfig: PositionEnum = PositionEnum.center;

  /**
   * Pass the parent themesConfig to manage current theme for component
   */
  @Input()
  parentsThemesConfig: ThemesEnum | BrandThemesEnum = ThemesEnum.light;

  @Input()
  isBrand = false;

  @Input()
  hasSubline = true;

  @Input()
  title: string;

  @Input()
  type: PageTypeEnum = PageTypeEnum.footer;

  @Output()
  clickedReinsurance = new EventEmitter<string>();

  @Output()
  isOpenedChange = new EventEmitter<boolean>();

  @Input() class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.contentThemesConfig && this.parentsThemesConfig
        ? 'is-theme-' + this.contentThemesConfig
        : '',
      this.parentsThemesConfig ? 'has-parent-' + this.parentsThemesConfig : '',
      this.type ? 'is-type-' + this.type : 'is-type-footer'
    ]
      .filter(Boolean)
      .join(' ');
  }

  manageThemesTextConfigUtils = ThemesUtils.manageThemesTextConfig;
  contentThemesConfig: ThemesTextEnum;
  pageTypeEnum = PageTypeEnum;
  positionEnum = PositionEnum;
  iconSizeEnum = IconSizeEnum;
  hasModal = false;
  isOpened = false;
  modalContent: IReinsurancesModal;
  bodyChildren;

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    this.manageThemesTextConfig(this.parentsThemesConfig);
  }

  ngOnInit() {
    if (this.reinsurances) {
      this.reinsurances.forEach((reinsurance) => {
        if (reinsurance.subtitle) {
          this.hasModal = true;
        }
      });
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.bodyChildren = this.document.body.children;
    }
  }

  manageThemesTextConfig(theme: ThemesEnum | BrandThemesEnum) {
    this.contentThemesConfig = this.manageThemesTextConfigUtils(theme);
  }

  onClick(event) {
    this.clickedReinsurance.emit(event);
  }

  openModal() {
    const _reinsurances: IReinsuranceModal[] = [];

    this.reinsurances.forEach((reinsurance) => {
      _reinsurances.push({
        title: reinsurance?.title ? reinsurance.title : null,
        subtitle: reinsurance?.subtitle ? reinsurance.subtitle : null,
        icons: reinsurance?.icons ? reinsurance.icons : null
      });
    });

    this.isOpened = true;
    this.modalContent = {
      title: this.title,
      reinsurances: _reinsurances
    };

    /*
     * A11y
     */
    for (const child of this.bodyChildren) {
      if (child.className !== 'cdk-overlay-container') {
        child.setAttribute('aria-hidden', true);
      }
    }
  }
}
